/**
 * PanelIncidencias.tsx — INFDOC-2b: ventana con el detalle, fila a fila, de las
 * incidencias del documento cargado en P1 (se abre al pulsar la pastilla).
 *
 * DOP selladas por Luis (3-nov-2026, una a una): ventana modal con Radix Dialog
 * y los colores del DS (1 c); apartados «Para el ayuntamiento» y «Solo para el
 * GREA» con las cifras del resumen, por posición en el documento; la fila con
 * incidencias de los dos lados va al del ayuntamiento con todas ellas (2 b);
 * columnas Fila · Elemento · Incidencia · Clase (3 c); distancia y filas con
 * las que comparte posición, sin UTM ni nombres de fuentes (4 b); sin
 * descargas hasta INFDOC-3 (5 b). Sin enlace a la tabla (DOP-4 a de INFDOC-2).
 *
 * INFDOC-3b2 (Luis, 4-nov-2026, una a una): «Descargar informe para el
 * ayuntamiento (PDF)» en el pie, a la izquierda de «Cerrar», como principal
 * (DOP-1 a); mientras se prepara, deshabilitado con «Preparando el informe…» y,
 * si se cierra la ventana a mitad, el PDF se descarga igual (DOP-3 a); aviso
 * solo si faltan ortofotos o falla el PDF (DOP-4 a). Sin la orden de descarga
 * no hay botón.
 *
 * Solo pinta: el detalle lo calcula `Step1Page` (`detallarIncidencias`), y la
 * descarga también (`descargarInformeAyuntamiento`).
 *
 * @module wizard-v2/step1-normaliza/PanelIncidencias
 */
import React, { useRef, useState, type FC, type CSSProperties } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
// Por ruta de fichero, no por el barril (HX-MOCK-FACTORY-BARREL-COUPLING)
import { colors, typography, spacing, borderRadius, shadows, zIndex } from '@/design-system/v2/design-tokens';

// ─── Tipos ──────────────────────────────────────────────────────────────────

/** Clase a la vista; «solo GREA» también marca, en el apartado del ayuntamiento, la que no le irá */
export type EtiquetaClase = 'comprobada' | 'por confirmar' | 'solo GREA';

export interface IncidenciaDeFila {
  /** Texto llano del tipo (el de `TEXTO_INCIDENCIA`), con mayúscula inicial */
  texto: string;
  /** Dato que la acompaña: distancia o filas con las que comparte (DOP-4 b) */
  apunte?: string;
  clase: EtiquetaClase;
}

export interface FilaConIncidencias {
  /** Número de fila tal como lo enseña la tabla de P1 (1 = primera) */
  fila: number;
  elemento: string;
  incidencias: IncidenciaDeFila[];
}

export interface ApartadoIncidencias {
  titulo: string;
  filas: FilaConIncidencias[];
  /** Incidencias del documento entero, sin fila (p. ej., «el documento no indica el municipio») */
  notas?: string[];
}

export interface DetalleIncidencias {
  apartados: ApartadoIncidencias[];
  /** Filas sin comprobar el límite del término (DOP-5 de INFDOC-2); ausente si no hay */
  aviso?: string;
}

/** INFDOC-3b2: lo que devuelve la descarga del PDF del ayuntamiento */
export interface ResultadoDescargaInforme {
  /** Puntos del informe con ortofoto pedida (sin repetir) */
  puntos: number;
  /** De ellos, los que salen sin ortofoto (solo con el enlace a Google Maps) */
  sinOrtofoto: number;
}

export interface PanelIncidenciasProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  /** Filas con alguna incidencia (la cifra de la pastilla) */
  filas: number;
  detalle: DetalleIncidencias;
  /** Al cerrar: quien abre decide adónde va el foco (la pastilla, sin reabrir el resumen) */
  onCloseAutoFocus?: (e: Event) => void;
  /** INFDOC-3b2: prepara y descarga el PDF del ayuntamiento; sin ella no hay botón */
  onDescargarInforme?: () => Promise<ResultadoDescargaInforme>;
}

// ─── Estilos (DS v2) ────────────────────────────────────────────────────────

const COLOR_CLASE: Record<EtiquetaClase, string> = {
  comprobada: colors.fg.secondary,
  'por confirmar': colors.warning,
  'solo GREA': colors.fg.muted,
};

const S = {
  velo: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    zIndex: zIndex.modal,
  } as CSSProperties,
  ventana: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: zIndex.modal,
    width: 'min(900px, calc(100vw - 32px))',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    background: colors.bg.secondary,
    color: colors.fg.primary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.lg,
    boxShadow: shadows.xl,
    outline: 'none',
  } as CSSProperties,
  cabecera: {
    padding: `${spacing[4]} ${spacing[5]} ${spacing[3]}`,
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  } as CSSProperties,
  titulo: {
    margin: 0,
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    color: colors.fg.primary,
  } as CSSProperties,
  subtitulo: { margin: `${spacing[1]} 0 0`, fontSize: '13px', color: colors.fg.secondary } as CSSProperties,
  aviso: { margin: `${spacing[2]} 0 0`, fontSize: '13px', color: colors.warning } as CSSProperties,
  cuerpo: { overflowY: 'auto', padding: `0 ${spacing[5]} ${spacing[3]}` } as CSSProperties,
  apartado: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    margin: `${spacing[4]} 0 ${spacing[1]}`,
    paddingBottom: spacing[1],
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
    fontSize: '13px',
    fontWeight: typography.fontWeight.semibold,
  } as CSSProperties,
  nota: { margin: `${spacing[1]} 0`, fontSize: '13px', color: colors.fg.secondary } as CSSProperties,
  // Ancho fijo: con el automático, los nombres largos de Guadix (hasta 133 letras)
  // se comían la tabla y partían «Incidencia» en dos líneas (medido en dev, 3-nov)
  tabla: { width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '13px', lineHeight: 1.4 } as CSSProperties,
  th: {
    position: 'sticky',
    top: 0,
    background: colors.bg.secondary,
    textAlign: 'left',
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.muted,
    padding: `${spacing[2]} ${spacing[2]} ${spacing[1]}`,
    whiteSpace: 'nowrap',
  } as CSSProperties,
  td: { padding: `3px ${spacing[2]}`, verticalAlign: 'top', color: colors.fg.secondary, overflowWrap: 'anywhere' } as CSSProperties,
  mono: {
    fontFamily: typography.fontFamily.mono,
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.primary,
    textAlign: 'right',
  } as CSSProperties,
  pie: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: spacing[3],
    padding: `${spacing[3]} ${spacing[5]}`,
    borderTop: `1px solid ${colors.border.DEFAULT}`,
  } as CSSProperties,
  avisoPie: { flex: 1, margin: 0, fontSize: '13px', lineHeight: 1.4 } as CSSProperties,
  // INFDOC-3b2 (DOP-1 a): principal, como el de FooterBar
  descargar: (preparando: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing[2],
    padding: `${spacing[1]} ${spacing[4]}`,
    borderRadius: borderRadius.md,
    border: 'none',
    background: colors.primary,
    color: colors.onPrimary,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    cursor: preparando ? 'progress' : 'pointer',
    opacity: preparando ? 0.7 : 1,
    whiteSpace: 'nowrap',
  }),
  cerrar: {
    padding: `${spacing[1]} ${spacing[4]}`,
    borderRadius: borderRadius.md,
    border: `1px solid ${colors.border.DEFAULT}`,
    background: colors.bg.tertiary,
    color: colors.fg.primary,
    fontSize: typography.fontSize.sm,
    cursor: 'pointer',
  } as CSSProperties,
};

const filasTxt = (n: number) => `${n} ${n === 1 ? 'fila' : 'filas'}`;

export const TEXTO_DESCARGAR = 'Descargar informe para el ayuntamiento (PDF)';
export const TEXTO_PREPARANDO = 'Preparando el informe…';
export const TEXTO_ERROR_INFORME = 'No se pudo preparar el informe.';

/** INFDOC-3b2 (DOP-4 a): aviso tras descargar; nada si no falta ninguna ortofoto */
export function avisoDescarga({ puntos, sinOrtofoto }: ResultadoDescargaInforme): string | undefined {
  if (sinOrtofoto <= 0) return undefined;
  return `Informe descargado. ${sinOrtofoto} de ${puntos} ${puntos === 1 ? 'punto' : 'puntos'} `
    + `${sinOrtofoto === 1 ? 'va' : 'van'} sin ortofoto, solo con el enlace a Google Maps. `
    + 'Puede volver a descargarlo para intentarlo de nuevo.';
}

type EstadoDescarga =
  | { fase: 'quieto' }
  | { fase: 'preparando' }
  | { fase: 'hecho'; aviso?: string }
  | { fase: 'error' };

// ─── Componente ─────────────────────────────────────────────────────────────

const Apartado: FC<{ apartado: ApartadoIncidencias }> = ({ apartado }) => (
  <section data-testid="panel-incidencias-apartado" aria-label={apartado.titulo}>
    <div style={S.apartado}>
      <span>{apartado.titulo}</span>
      <span style={{ ...S.mono, color: colors.warning }}>{filasTxt(apartado.filas.length)}</span>
    </div>
    {apartado.notas?.map(n => <p key={n} style={S.nota}>{n}</p>)}
    {apartado.filas.length > 0 && (
      <table style={S.tabla}>
        <thead>
          <tr>
            <th style={{ ...S.th, width: '48px', textAlign: 'right' }}>Fila</th>
            <th style={{ ...S.th, width: '42%' }}>Elemento</th>
            <th style={S.th}>Incidencia</th>
            <th style={{ ...S.th, width: '104px' }}>Clase</th>
          </tr>
        </thead>
        <tbody>
          {apartado.filas.map(f => f.incidencias.map((inc, k) => (
            <tr
              key={`${f.fila}-${k}`}
              data-testid={k === 0 ? 'panel-incidencias-fila' : undefined}
              data-fila={k === 0 ? f.fila : undefined}
              style={k === 0 ? { borderTop: `1px solid ${colors.border.light}` } : undefined}
            >
              {k === 0 && (
                <>
                  <td rowSpan={f.incidencias.length} style={{ ...S.td, ...S.mono }}>{f.fila}</td>
                  <td rowSpan={f.incidencias.length} style={{ ...S.td, color: colors.fg.primary }}>{f.elemento}</td>
                </>
              )}
              <td style={S.td}>
                {inc.texto}
                {inc.apunte && <span style={{ color: colors.fg.muted }}> · {inc.apunte}</span>}
              </td>
              <td style={{ ...S.td, color: COLOR_CLASE[inc.clase], whiteSpace: 'nowrap' }}>{inc.clase}</td>
            </tr>
          )))}
        </tbody>
      </table>
    )}
  </section>
);

/** INFDOC-2b: ventana modal con el detalle por fila (Esc, «Cerrar» o clic fuera la cierran) */
export const PanelIncidencias: FC<PanelIncidenciasProps> = ({
  abierto, onAbiertoChange, filas, detalle, onCloseAutoFocus, onDescargarInforme,
}) => {
  const [descarga, setDescarga] = useState<EstadoDescarga>({ fase: 'quieto' });
  // Candado síncrono: dos pulsaciones seguidas no piden dos PDF (DOP-3 a)
  const enCurso = useRef(false);
  const descargar = async () => {
    if (!onDescargarInforme || enCurso.current) return;
    enCurso.current = true;
    setDescarga({ fase: 'preparando' });
    try {
      setDescarga({ fase: 'hecho', aviso: avisoDescarga(await onDescargarInforme()) });
    } catch (err) {
      // HX-FALLBACK-ENMASCARA-BUG: se dice en pantalla y queda en la consola
      console.warn('[INFDOC-3b2] No se pudo preparar el informe del ayuntamiento:', err);
      setDescarga({ fase: 'error' });
    } finally {
      enCurso.current = false;
    }
  };
  const preparando = descarga.fase === 'preparando';
  const aviso = descarga.fase === 'error' ? TEXTO_ERROR_INFORME : descarga.fase === 'hecho' ? descarga.aviso : undefined;
  return (
  <DialogPrimitive.Root open={abierto} onOpenChange={onAbiertoChange}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay data-testid="panel-incidencias-velo" style={S.velo} />
      <DialogPrimitive.Content data-testid="panel-incidencias" style={S.ventana} onCloseAutoFocus={onCloseAutoFocus}>
        <div style={S.cabecera}>
          <DialogPrimitive.Title style={S.titulo}>Incidencias del documento</DialogPrimitive.Title>
          <DialogPrimitive.Description style={S.subtitulo}>
            {filasTxt(filas)} con incidencias. Una fila puede tener varias.
          </DialogPrimitive.Description>
          {detalle.aviso && <p data-testid="panel-incidencias-aviso" style={S.aviso}>{detalle.aviso}</p>}
        </div>
        <div style={S.cuerpo}>
          {detalle.apartados.map(a => <Apartado key={a.titulo} apartado={a} />)}
        </div>
        <div data-testid="panel-incidencias-pie" style={S.pie}>
          {onDescargarInforme && (
            <>
              <p
                data-testid="panel-incidencias-aviso-descarga"
                role="status"
                aria-live="polite"
                style={{ ...S.avisoPie, color: descarga.fase === 'error' ? colors.error : colors.warning }}
              >
                {aviso}
              </p>
              <button
                type="button"
                data-testid="panel-incidencias-descargar"
                disabled={preparando}
                aria-busy={preparando}
                onClick={() => { void descargar(); }}
                style={S.descargar(preparando)}
              >
                {preparando && <Loader2 size={14} className="animate-spin" aria-hidden />}
                {preparando ? TEXTO_PREPARANDO : TEXTO_DESCARGAR}
              </button>
            </>
          )}
          <DialogPrimitive.Close style={S.cerrar}>Cerrar</DialogPrimitive.Close>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
  );
};

export default PanelIncidencias;
